import { createContext, useContext, useRef, type ReactNode } from 'react';

import type { PromptEditorHandle } from './prompt-editor';
import type { FileAttachment } from '../../../../hooks/use-file-selection';

export interface ChatInputContextValue {
  // Input state
  input: string;
  setInput: (value: string) => void;

  // Editor ref (can be textarea or contenteditable div)
  editorRef: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
  promptEditorRef: React.RefObject<PromptEditorHandle | null>;

  // Submit handler
  handleSubmit: (
    e?: React.FormEvent<HTMLFormElement>,
    manualInput?: string,
    /** Replaces the overlay's additional request context for this submission. */
    manualAdditionalContext?: Record<string, unknown>,
  ) => Promise<void> | void;

  // Status
  status: string;

  // Scroll state
  isAtBottom: boolean;
  scrollToBottom: () => void;

  // Public mode
  isPublicMode?: boolean;

  // Maximum allowed message length in characters. When set, submission is blocked
  // beyond this limit across every submit path (Enter key, send button, form submit,
  // programmatic). Undefined means no limit.
  maxLength?: number;
}

/**
 * Additional context that toolbar components can contribute to the message payload.
 * This allows components like ChatAttachments or other toolbar items to add data
 * that will be included when the message is sent.
 */
export interface MessagePayloadContext {
  // File attachments (from ChatAttachments)
  attachments?: FileAttachment[];

  // File UUIDs for the API request (from ChatAttachments)
  fileUuids?: string[];

  // Additional metadata for the message (from ChatAttachments)
  fileMetadata?: {
    uuid: string;
    filename: string;
  }[];

  // Additional context for the API request (set by toolbar components like ChatAttachments)
  // Components can add their own keys here, and the framework will pass it through to the transport
  additionalContext?: Record<string, unknown>;

  // Additional API request body data (for other fields beyond additionalContext)
  // Other toolbar components can add their own keys here
  additionalBodyData?: Record<string, unknown>;
}

/**
 * Drag/drop handlers that ChatAttachments can provide
 */
export interface DragHandlers {
  isDragging?: boolean;
  handleDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave?: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
  handlePaste?: (event: React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>) => void;
}

const ChatInputContext = createContext<ChatInputContextValue | undefined>(undefined);
const MessagePayloadContext = createContext<
  | {
      payload: MessagePayloadContext;
      updatePayload: (updates: Partial<MessagePayloadContext>) => void;
    }
  | undefined
>(undefined);

// Use a ref-based approach for drag handlers to avoid re-renders
const DragHandlersRefContext = createContext<React.MutableRefObject<DragHandlers>>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  null as any,
);

interface ChatInputProviderProps {
  children: ReactNode;
  value: ChatInputContextValue;
}

/**
 * Provides chat input state to child components.
 * This allows composable input components to access shared state without prop drilling.
 */
export function ChatInputProvider({ children, value }: ChatInputProviderProps) {
  return <ChatInputContext.Provider value={value}>{children}</ChatInputContext.Provider>;
}

/**
 * Hook to access chat input context.
 * @throws Error if used outside of ChatInputProvider
 */
export function useChatInputContext(): ChatInputContextValue {
  const context = useContext(ChatInputContext);
  if (!context) {
    throw new Error('useChatInputContext must be used within ChatInputProvider');
  }
  return context;
}

/**
 * Hook to access drag handlers ref (for ChatInput to read handlers)
 */
export function useDragHandlersRef() {
  const ref = useContext(DragHandlersRefContext);
  if (!ref) {
    // Return a ref with empty handlers as fallback
    return { current: {} } as React.MutableRefObject<DragHandlers>;
  }
  return ref;
}

/**
 * Hook for components to update drag handlers (for ChatAttachments to provide handlers)
 */
export function useDragHandlers() {
  const handlersRef = useDragHandlersRef();

  return {
    setHandlers: (handlers: DragHandlers) => {
      if (handlersRef) {
        handlersRef.current = handlers;
      }
    },
  };
}

/**
 * Provider for drag handlers ref.
 * This allows ChatAttachments to provide drag handlers without MessagePayloadProvider.
 */
export function DragHandlersProvider({ children }: { children: ReactNode }) {
  const dragHandlersRef = useRef<DragHandlers>({});

  return <DragHandlersRefContext.Provider value={dragHandlersRef}>{children}</DragHandlersRefContext.Provider>;
}
